import type {
  BootstrapData,
  DocumentFileType,
  DocumentRecord,
  DocumentShare,
  KnowledgeBaseData,
  User,
  UserRole,
  ManagedUser,
  DocumentVersionRecord,
  DocumentVersionSummary,
  PublicDocumentRecord,
  KnowledgeBaseMemberAccess,
  AdminSystemSettings,
  PublicSystemSettings,
} from '../types';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers);
  if (options?.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(path, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as {
    message?: string;
  };
  if (!response.ok) {
    throw new ApiError(payload.message ?? '请求失败，请稍后重试。', response.status);
  }
  return payload as T;
}

export const api = {
  publicSettings() {
    return request<{ settings: PublicSystemSettings }>('/api/public/settings');
  },
  login(email: string, password: string) {
    return request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  me() {
    return request<{ user: User }>('/api/auth/me');
  },
  logout() {
    return request<void>('/api/auth/logout', { method: 'POST' });
  },
  requestPasswordReset(email: string) {
    return request<{ message: string; expiresIn: number }>('/api/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  confirmPasswordReset(input: { email: string; code: string; password: string }) {
    return request<{ message: string }>('/api/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  bootstrap() {
    return request<BootstrapData>('/api/bootstrap');
  },
  createKnowledgeBase(input: {
    kind: 'personal' | 'team';
    name: string;
    description: string;
    visibility: 'private' | 'workspace' | 'public';
    color: string;
  }) {
    return request<{ id: string; slug: string }>('/api/knowledge-bases', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  knowledgeBase(id: string) {
    return request<KnowledgeBaseData>(`/api/knowledge-bases/${id}/documents`);
  },
  knowledgeBaseAccess(id: string) {
    return request<{ members: KnowledgeBaseMemberAccess[] }>(
      `/api/knowledge-bases/${id}/access`,
    );
  },
  updateKnowledgeBaseAccess(
    id: string,
    members: Array<{ userId: string; permission: 'none' | 'view' | 'edit' }>,
  ) {
    return request<{ updatedAt: string }>(`/api/knowledge-bases/${id}/access`, {
      method: 'PUT',
      body: JSON.stringify({ members }),
    });
  },
  createFile(
    knowledgeBaseId: string,
    fileType: DocumentFileType,
    title = fileType === 'spreadsheet' ? '无标题表格' : '无标题文档',
    parentId?: string | null,
  ) {
    return request<{ id: string }>(`/api/knowledge-bases/${knowledgeBaseId}/documents`, {
      method: 'POST',
      body: JSON.stringify({ title, fileType, parentId: parentId ?? null }),
    });
  },
  createDocument(knowledgeBaseId: string, title = '无标题文档', parentId?: string | null) {
    return request<{ id: string }>(`/api/knowledge-bases/${knowledgeBaseId}/documents`, {
      method: 'POST',
      body: JSON.stringify({ title, parentId: parentId ?? null }),
    });
  },
  document(id: string) {
    return request<{ document: DocumentRecord }>(`/api/documents/${id}`);
  },
  uploadDocumentImage(id: string, file: File) {
    return request<{
      asset: {
        id: string;
        url: string;
        mimeType: string;
        sizeBytes: number;
        originalName: string;
      };
    }>(`/api/documents/${id}/assets/images`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        'X-File-Name': encodeURIComponent(file.name),
      },
      body: file,
    });
  },
  documentShare(id: string) {
    return request<{ share: DocumentShare | null }>(`/api/documents/${id}/share`);
  },
  createDocumentShare(id: string) {
    return request<{ share: DocumentShare }>(`/api/documents/${id}/share`, {
      method: 'POST',
    });
  },
  resetDocumentShare(id: string) {
    return request<{ share: DocumentShare }>(`/api/documents/${id}/share/reset`, {
      method: 'POST',
    });
  },
  publicDocument(shareToken: string) {
    return request<{ document: PublicDocumentRecord }>(
      `/api/public/documents/${encodeURIComponent(shareToken)}`,
    );
  },
  saveDocument(
    id: string,
    input: {
      title: string;
      contentJson: unknown;
      contentText: string;
      status?: 'draft' | 'published';
      createVersion?: boolean;
      versionSummary?: string;
    },
  ) {
    return request<{ updatedAt: string }>(`/api/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  collaborationToken(id: string) {
    return request<{ token: string; expiresIn: number; readOnly: boolean }>(
      `/api/documents/${id}/collaboration-token`,
      { method: 'POST' },
    );
  },
  documentVersions(id: string) {
    return request<{ versions: DocumentVersionSummary[] }>(`/api/documents/${id}/versions`);
  },
  documentVersion(id: string, version: number) {
    return request<{ version: DocumentVersionRecord }>(
      `/api/documents/${id}/versions/${version}`,
    );
  },
  restoreDocumentVersion(id: string, version: number) {
    return request<{ updatedAt: string; version: number }>(
      `/api/documents/${id}/versions/${version}/restore`,
      { method: 'POST' },
    );
  },
  search(q: string) {
    return request<{
      results: Array<{
        id: string;
        title: string;
        fileType: DocumentFileType;
        updatedAt: string;
        knowledgeBaseId: string;
        knowledgeBaseName: string;
      }>;
    }>(`/api/search?q=${encodeURIComponent(q)}`);
  },
  users() {
    return request<{ users: ManagedUser[] }>('/api/admin/users');
  },
  systemSettings() {
    return request<{ settings: AdminSystemSettings }>('/api/admin/settings');
  },
  updateSystemSettings(input: {
    projectName: string;
    logoDataUrl: string | null;
    smtpHost: string;
    smtpPort: number;
    smtpSecurity: 'tls' | 'starttls' | 'none';
    smtpUsername: string;
    smtpPassword?: string;
    clearSmtpPassword?: boolean;
    mailFromName: string;
    mailFromAddress: string;
  }) {
    return request<{ settings: AdminSystemSettings }>('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  testSystemEmail() {
    return request<{ message: string }>('/api/admin/settings/test-email', { method: 'POST' });
  },
  createUser(input: { name: string; email: string; password: string; role: UserRole }) {
    return request<{ id: string }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};
